import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import PublicNav from '../components/PublicNav';
import { Heart, Users, Rocket, Mail, ArrowRight } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>About | Survivor Fantasy League</title>
        <meta name="description" content="Survivor Fantasy League is a free platform built for Survivor fans to run fantasy leagues with friends. Learn about the platform and the people behind it." />
        <link rel="canonical" href="https://survivorfantasy.live/about" />
        <meta property="og:title" content="About | Survivor Fantasy League" />
        <meta property="og:description" content="Survivor Fantasy League is a free platform built for Survivor fans to run fantasy leagues with friends." />
        <meta property="og:url" content="https://survivorfantasy.live/about" />
      </Helmet>

      <PublicNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#4682B4' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Built for the Fans
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
            What this is, why it exists, and where it's going.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Section 1: What is it */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF7F50' }}>
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                What is Survivor Fantasy League?
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Survivor Fantasy League is a free platform for running season-long fantasy leagues tied to the CBS show Survivor. You draft real castaways, earn points based on what they do in the game, compete against friends on a live leaderboard, and answer weekly bonus questions to prove you actually know what's happening out there.
              </p>
              <p>
                It's the experience of watching Survivor with friends — with something real at stake.
              </p>
            </div>
          </section>

          {/* Section 2: Why This Exists */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4682B4' }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                Why This Exists
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Survivor has one of the most passionate fan communities in reality TV. People watch together, argue about strategy, and have strong opinions about who's playing the best game. But until now, there wasn't a purpose-built platform for running a proper fantasy league around it.
              </p>
              <p>
                Most groups were using spreadsheets, Google Forms, and group chats to track points manually. It worked, but it was messy, error-prone, and required someone to spend hours maintaining it. Survivor Fantasy League automates all of that — the draft, the scoring math, the leaderboard, the notifications — so the League Admin can focus on watching the show, not managing a spreadsheet.
              </p>
            </div>
          </section>

          {/* Section 3: Who's Behind It */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1E3A4C' }}>
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                Who's Behind It
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                Survivor Fantasy League is an independent project built and maintained by a single developer. It's not affiliated with CBS, the Survivor brand, or any production company.
              </p>
              <p>
                It's built for every season of Survivor and designed to grow with each new one. Early users get access to new features first and have direct input on what gets built next.
              </p>
              <p>
                Feedback is always welcome at <a href="mailto:support@survivorfantasy.live" className="text-[#FF7F50] underline hover:opacity-80">support@survivorfantasy.live</a>.
              </p>
            </div>
          </section>

          {/* Section 4: What's Coming */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FF7F50' }}>
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                What's Coming
              </h2>
            </div>
            <div className="prose prose-lg max-w-none" style={{ color: '#4682B4' }}>
              <p>
                The platform is actively being developed. Features on the roadmap include a dedicated mobile experience, improved admin tooling, enhanced statistics, and expanded multi-season support. Early access users get new features as they ship.
              </p>
              <p>
                If you have a feature request or found something broken, send an email. It'll be read.
              </p>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center py-8 px-6 rounded-2xl" style={{ backgroundColor: '#1E3A4C' }}>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to join the tribe?
            </h3>
            <p className="mb-6" style={{ color: '#F5F0E6' }}>
              Create a free account and start your first league today.
            </p>
            <Link to="/login?tab=register">
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: '#FF7F50' }}>
                Sign Up Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/how-it-works" className="hover:text-[#FF7F50] transition-colors">How It Works</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
